// frontend/src/pages/games/SudokuGame.tsx

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSudokuGame } from '../../hooks/games/useSudokuGame';
import { useGameSessionTracking } from '../../hooks/games/useGameSessionTracking';
import { SudokuBoard } from '../../components/games/sudoku/SudokuBoard';
import { SudokuNumberPad } from '../../components/games/sudoku/SudokuNumberPad';
import { SudokuDifficultySelector } from '../../components/games/sudoku/SudokuDifficultySelector';
import { GameCompleteModal } from '../../components/games/GameCompleteModal';
import '../../styles/games.css';
import '../../styles/games-sudoku.css';

const GAME_ID = 'sudoku';
const SUDOKU_COMPLETE_XP = 20;

export function SudokuGamePage() {
  const navigate = useNavigate();
  const {
    difficulty,
    cells,
    selected,
    mistakes,
    isComplete,
    selectCell,
    inputNumber,
    eraseCell,
    startNewGame,
  } = useSudokuGame('easy');

  const { recordCompletion, resetRoundTimer } = useGameSessionTracking(GAME_ID);
  const recordedRef = useRef(false);
  const [xpEarned, setXpEarned] = useState<number | null>(null);

  useEffect(() => {
    if (isComplete && !recordedRef.current) {
      recordedRef.current = true;
      const score = Math.max(0, 1000 - mistakes * 50);
      const { xpAwarded } = recordCompletion({
        score,
        cumulativeDeltas: { puzzlesCompleted: 1 },
        xpAmount: SUDOKU_COMPLETE_XP,
      });
      setXpEarned(xpAwarded);
    }
    if (!isComplete) {
      recordedRef.current = false;
    }
  }, [isComplete, mistakes, recordCompletion]);

  const handleRestart = (nextDifficulty?: typeof difficulty) => {
    resetRoundTimer();
    setXpEarned(null);
    startNewGame(nextDifficulty);
  };

  return (
    <div className="game-center-page sudoku-game-page">
      <header className="game-center-header">
        <div>
          <h1>🧩 Sudoku</h1>
          <p>Bir hücre seç, sonra bir sayı gir. Her satır, sütun ve 3x3 kutuda 1-9 bir kez bulunmalı.</p>
        </div>
        <Link to="/games" className="game-card-btn secondary sm">
          ← Oyun Merkezi
        </Link>
      </header>

      <div className="sudoku-toolbar">
        <SudokuDifficultySelector current={difficulty} onSelect={(d) => handleRestart(d)} />

        <div className="sudoku-stats">
          <span>❌ Hata: {mistakes}</span>
        </div>

        <button type="button" className="game-card-btn secondary sm" onClick={() => handleRestart()}>
          🔄 Yeni Bulmaca
        </button>
      </div>

      <SudokuBoard cells={cells} selected={selected} onSelect={selectCell} />

      <SudokuNumberPad cells={cells} onInput={inputNumber} onErase={eraseCell} />

      {isComplete && (
        <GameCompleteModal
          emoji="🧩"
          message={`Bulmacayı ${mistakes} hatayla tamamladın!`}
          xpEarned={xpEarned ?? undefined}
          onPlayAgain={() => handleRestart()}
          onReturnToCenter={() => navigate('/games')}
        />
      )}
    </div>
  );
}

